import { ProcessedProject } from "@/lib/types/github";
import GlassCard, { glassCardVariants } from "../glass-card";
import { Code2, ExternalLink, Github } from "lucide-react";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn, slugify, slugToNounPhrase } from "@/lib/utils";

export default function ProjectCard({
  project,
}: {
  project: ProcessedProject;
}) {
  return (
    <GlassCard
      variant="strong"
      blur="lg"
      glow="medium"
      hover
      className={`p-6 group relative  bg-[${project.colors.primary}]`}
      style={
        {
          "--color-primary": project.colors.primary,
          "--color-secondary": project.colors.secondary,
        } as React.CSSProperties
      }
    >
      {/* Project Image */}
      <div className="aspect-video rounded-lg mb-4 overflow-hidden relative">
        <div
          className="w-full h-full flex items-center justify-center relative"
          style={{
            background: `linear-gradient(135deg, rgba(var(--color-primary), 0.1), rgba(var(--color-secondary), 0.1))`,
          }}
        >
          <Image
            src={project.image}
            fill
            className={`w-full h-full object-center object-cover ${project.hasImage ? "" : "opacity-50"}`}
            alt="Project Card Image"
          />
        </div>

        {/* Overlay gradient */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: `linear-gradient(to top, rgba(var(--color-primary), 0.4), transparent)`,
          }}
        />
      </div>

      {/* Project Title */}
      <h3 className="text-outline-fg-primary-soft text-xl font-bold mb-2">
        {slugToNounPhrase(project.name)}
      </h3>

      {/* Project Description */}
      <p className="text-sm mb-4 line-clamp-3">{project.description}</p>

      {/* Languages/Technologies */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {project.languages.map((lang) => (
          <Badge
            key={lang}
            className={cn("border-foreground", `t-badge ${slugify(lang)}`)}
          >
            {lang}
          </Badge>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mt-auto">
        {project.projectUrl && (
          <Button size="sm" variant="secondary" className="flex-1" asChild>
            <a
              href={project.projectUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View
            </a>
          </Button>
        )}

        <Button
          size="sm"
          variant="outline"
          className={project.projectUrl ? "flex-1" : "w-full"}
          style={{
            borderColor: `var(--color-primary)`,
          }}
          asChild
        >
          <a href={project.repoUrl} target="_blank" rel="noopener noreferrer">
            <Github className="h-4 w-4 mr-2" />
            Repo
          </a>
        </Button>
      </div>
    </GlassCard>
  );
}

export function ProjectCardSkeleton() {
  return (
    <GlassCard
      variant="strong"
      blur="lg"
      glow="medium"
      className="p-6 animate-pulse"
    >
      <div className="aspect-video bg-primary/10 rounded-lg mb-4" />
      <div className="h-6 bg-primary/10 rounded mb-2" />
      <div className="h-20 bg-primary/10 rounded mb-4" />
      <div className="flex gap-2">
        <div className="h-6 w-16 bg-primary/10 rounded" />
        <div className="h-6 w-16 bg-primary/10 rounded" />
      </div>
    </GlassCard>
  );
}
